/**
 * Helper class for managing price display in the UI
 */
export default class PriceDisplay {
    /**
     * Create a new PriceDisplay instance
     * 
     * @param {HTMLElement} container - Container element for price display
     * @param {Object} options - Display options
     */
    constructor(container, options = {}) {
        this.container = container;
        this.options = {
            animationDuration: 500,
            animationSteps: 20,
            showDetailedBreakdown: true,
            ...options
        };
        
        this.currentPrice = {
            base: 0,
            extras: 0,
            total: 0
        };
        
        this.initializeDisplay();
    }
    
    /**
     * Initialize price display elements
     */
    initializeDisplay() {
        // Create price summary container if it doesn't exist
        this.container.innerHTML = `
            <div class="stc-price-row">
                <span>Base Price</span>
                <span class="stc-price-base">$0.00</span>
            </div>
            <div class="stc-price-row">
                <span>Extra Features</span>
                <span class="stc-price-extras">$0.00</span>
            </div>
            <div class="stc-price-row stc-price-total">
                <span>Total Price</span>
                <span class="stc-price-total-value">$0.00</span>
            </div>
            ${this.options.showDetailedBreakdown ? `
                <div class="stc-price-breakdown-toggle">
                    Show detailed breakdown
                </div>
                <div class="stc-price-breakdown-details">
                    <!-- Detailed breakdown will be inserted here -->
                </div>
            ` : ''}
            <button class="stc-add-to-cart">Add to Cart</button>
        `;
        
        // Add toggle functionality if detailed breakdown is enabled
        if (this.options.showDetailedBreakdown) {
            const toggle = this.container.querySelector('.stc-price-breakdown-toggle');
            const details = this.container.querySelector('.stc-price-breakdown-details');
            
            toggle.addEventListener('click', () => {
                const isVisible = details.classList.toggle('is-visible');
                toggle.textContent = isVisible
                    ? 'Hide detailed breakdown'
                    : 'Show detailed breakdown';
            });
        }
    }
    
    /**
     * Update price display with new values
     * 
     * @param {Object} priceBreakdown - Price breakdown object
     * @param {boolean} animate - Whether to animate the update
     */
    updateDisplay(priceBreakdown, animate = true) {

        const baseElement = this.container.querySelector('.stc-price-base');
        const extrasElement = this.container.querySelector('.stc-price-extras');
        const totalElement = this.container.querySelector('.stc-price-total-value');

        if (!baseElement || !extrasElement || !totalElement) return;

        // Set new prices, with or without animation
        if (animate) {
            this.animatePrice(baseElement, priceBreakdown.basePrice);
            this.animatePrice(extrasElement, priceBreakdown.totalExtras, '+');
            this.animatePrice(totalElement, priceBreakdown.total);
        } else {
            baseElement.textContent = `$${priceBreakdown.basePrice.toFixed(2)}`;
            extrasElement.textContent = `+$${priceBreakdown.totalExtras.toFixed(2)}`;
            totalElement.textContent = `$${priceBreakdown.total.toFixed(2)}`;
        }
            
            // Update detailed breakdown if enabled
            if (this.options.showDetailedBreakdown) {
            this.updateDetailedBreakdown(priceBreakdown);
        }
        
        // Store current prices
        this.currentPrice = {
            base: priceBreakdown.basePrice,
            extras: priceBreakdown.totalExtras,
            total: priceBreakdown.total
        };
    }
    
    /**
     * Update detailed price breakdown display
     * 
     * @param {Object} priceBreakdown - Price breakdown object
     */
    updateDetailedBreakdown(priceBreakdown) {
        const detailsContainer = this.container.querySelector('.stc-price-breakdown-details');
        if (!detailsContainer) return;
        
        let html = '';
        
        // Add front side extras if any
        if (priceBreakdown.frontExtras > 0) {
            html += `
                <div class="stc-price-breakdown-item">
                    <span>Front Side Features <span class="stc-price-front-badge">Front</span></span>
                    <span>+$${priceBreakdown.frontExtras.toFixed(2)}</span>
                </div>
            `;
        }
        
        // Add back side extras if any
        if (priceBreakdown.backExtras > 0) {
            html += `
                <div class="stc-price-breakdown-item">
                    <span>Back Side Features <span class="stc-price-back-badge">Back</span></span>
                    <span>+$${priceBreakdown.backExtras.toFixed(2)}</span>
                </div>
            `;
        }
        
        detailsContainer.innerHTML = html;
    }
    
    /**
     * Animate price change
     * 
     * @param {HTMLElement} element - Element to animate
     * @param {number} newPrice - New price
     * @param {string} prefix - Price prefix
     */
    animatePrice(element, newPrice, prefix = '') {
        // Get current price from element
        const currentPrice = parseFloat(element.textContent.replace(/[^\d.-]/g, '')) || 0;
        
        // Skip animation if price hasn't changed
        if (currentPrice === newPrice) return;
        
        // Add highlight effect
        element.classList.add('stc-price-highlight');
        
        // Calculate animation steps
        const steps = this.options.animationSteps;
        const increment = (newPrice - currentPrice) / steps;
        let step = 0;
        
        // Animate price change
        const animate = () => {
            step++;
            const current = currentPrice + (increment * step);
            element.textContent = `${prefix}$${current.toFixed(2)}`;
            
            if (step < steps) {
                requestAnimationFrame(animate);
            } else {
                element.textContent = `${prefix}$${newPrice.toFixed(2)}`;
                
                // Remove highlight after animation completes
                setTimeout(() => {
                    element.classList.remove('stc-price-highlight');
                }, 500);
            }
        };
        
        requestAnimationFrame(animate);
    }
    
    /**
     * Get a formatted price string
     * 
     * @param {number} value - Price value
     * @param {Object} options - Formatting options
     * @returns {string} Formatted price
     */
    static formatPrice(value, options = {}) {
        const defaults = {
            prefix: '$',
            decimals: 2,
            showPlus: false
        };
        
        const settings = { ...defaults, ...options };
        
        const prefix = value > 0 && settings.showPlus ? '+' : '';
        return `${prefix}${settings.prefix}${Math.abs(value).toFixed(settings.decimals)}`;
    }
}